import * as Yup from "yup";
import {Message} from "yup/lib/types";
import DateUtils, {DateUtilsTypes} from "../../../core/services/date-utils";
import Reference from "yup/lib/Reference";
import Ref from "yup/lib/Reference";
import Regexes from "../../../core/models/regexes";

declare module "yup" {

    interface StringSchema {
        phoneNo(message?: Message, validLength?: number): StringSchema;

        emailAddress(message?: Message, required?: boolean): StringSchema;

        fractionNumber(message?: Message): StringSchema;
    }

    interface MixedSchema {
        validDate(message?: Message, required?: boolean): MixedSchema;

        minDate(minDate: unknown | Ref<DateUtilsTypes.Instance> | (() => unknown), message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema;

        maxDate(maxDate: unknown | Ref<DateUtilsTypes.Instance> | (() => unknown), message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema;

        disableFutureDate(message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema;

        disablePastDate(message?: Message, unit?: DateUtilsTypes.OpUnitType): MixedSchema;
    }

    interface NumberSchema {
        maxNumber(max: number | Ref<unknown>, message?: Message): NumberSchema;

        minNumber(min: number | Ref<unknown>, message?: Message): NumberSchema;
    }
}

/**
 * Houses all the customized test functions added to different types of the Yup package.
 *
 * - NOTE: be sure to add each of the necessary extensions in the files that you intend to use them.
 */
class YupExtensions {

    /**
     * Adds the phone number method to Yup String types.
     */
    public static phoneNo(): void {
        Yup.addMethod(Yup.string, 'phoneNo', function (...args) {
            const [message, validLength = 10] = args;
            return this.test('test-phone-no', message, function (val) {
                let value = val?.trim() ?? "";
                if (!val) {
                    return true;
                }
                value = value
                    .replace(/[()_-]/g, '')
                    .replace(/\+1/g, '')
                    .replaceAll(' ', '');
                if (!val) {
                    return true;
                }
                return value?.length === validLength;
            });
        });
    }

    /**
     * Adds the emailAddress method to Yup String types.
     */
    public static emailAddress(): void {
        Yup.addMethod(Yup.string, 'emailAddress', function (...args) {
            const [message, required = false] = args;
            return this.test('test-email-address', message, function (val) {
                let value = val?.trim();
                if (!val) {
                    return !required;
                }
                return !!value?.match(Regexes.Email);

            });
        });
    }


    /**
     * Adds the validDate method to Yup Mixed types that validates a date.
     */
    public static validDate(): void {
        Yup.addMethod(Yup.mixed, 'validDate', function (...args) {
            const [message, required = false] = args;
            return this.test({
                name: 'test-valid-date',
                message,
                exclusive: true,
                params: {
                    required,
                },
                test: (val) => {
                    if (!val) {
                        return !required;
                    }
                    if (!DateUtils.instance.isDayjs(val)) {
                        return false;
                    }
                    return val.isValid();
                }
            });
        });
    }


    /**
     * Adds the minDate method to Yup Mixed types that validates a date.
     */
    public static minDate(): void {
        Yup.addMethod(Yup.mixed, 'minDate', function (...args) {
            const [minDate, message, unit] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(minDate);
            return this.test({
                name: 'test-min-date',
                message: message,
                exclusive: true,
                params: {
                    min: limit,
                },
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    const minDateResolved = this.resolve(limit) as any;
                    if (!minDateResolved)
                        return true;
                    return val.isSameOrAfter(minDateResolved, unit);
                }
            });
        });
    }

    /**
     * Adds the maxDate method to Yup Mixed types that validates a date.
     */
    public static maxDate(): void {
        Yup.addMethod(Yup.mixed, 'maxDate', function (...args) {
            const [maxDate, message, unit] = args;

            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(maxDate);
            return this.test({
                name: 'test-max-date',
                message: message,
                exclusive: true,
                params: {
                    max: limit,
                },
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    const maxDateResolved = this.resolve(limit) as any;
                    if (!maxDateResolved)
                        return true;
                    return val.isSameOrBefore(maxDateResolved, unit);
                }
            });
        });
    }

    /**
     * Adds the disableFutureDate method to Yup Mixed types that validates a date.
     */
    public static disableFutureDate(): void {
        Yup.addMethod(Yup.mixed, 'disableFutureDate', function (...args) {
            const [message, unit] = args;
            return this.test({
                name: 'test-disable-future-date',
                message,
                exclusive: true,
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    return val.isSameOrBefore(DateUtils.localNowToUTC(), unit);
                }
            });
        });
    }

    /**
     * Adds the disablePastDate method to Yup Mixed types that validates a date.
     */
    public static disablePastDate(): void {
        Yup.addMethod(Yup.mixed, 'disablePastDate', function (...args) {
            const [message, unit] = args;
            return this.test({
                name: 'test-disable-past-date',
                message,
                exclusive: true,
                test: function (val) {
                    if (!val || !DateUtils.instance.isDayjs(val) || !DateUtils.UTC(val).isValid()) {
                        return true;
                    }
                    return val.local().isSameOrAfter(DateUtils.localNow(), unit);
                }
            });
        });
    }


    /**
     * Adds the numberMax method to Yup Number types.
     */
    public static maxNumber(): void {
        Yup.addMethod(Yup.number, 'maxNumber', function (...args) {
            const [max, message] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(max);
            return this.test({
                name: 'test-number-max',
                message: message,
                exclusive: true,
                params: {
                    max: limit,
                },
                test: function (val) {
                    const resolvedLimit = this.resolve(limit) as any;
                    if ((val === undefined || val === null) || (resolvedLimit === undefined || resolvedLimit === null)) {
                        return true;
                    }
                    return val <= resolvedLimit;
                }
            });
        });
    }

    /**
     * Adds the minNumber method to Yup Number types.
     */
    public static minNumber(): void {
        Yup.addMethod(Yup.number, 'minNumber', function (...args) {
            const [min, message] = args;
            const prepareParam = (ref: any) => {
                let param: any;
                if (!Reference.isRef(ref)) {
                    if (typeof ref === 'function') {
                        ref = ref();
                    }
                    param = this.cast(ref);
                } else {
                    param = ref;
                }
                return param;
            }
            const limit = prepareParam(min);
            return this.test({
                name: 'test-number-min',
                message: message,
                exclusive: true,
                params: {
                    min: limit,
                },
                test: function (val) {
                    const resolvedLimit = this.resolve(limit) as any;
                    if ((val === undefined || val === null) || (resolvedLimit === undefined || resolvedLimit === null)) {
                        return true;
                    }
                    return val >= resolvedLimit;
                }
            });
        });
    }

    public static fractionNumber(): void {
        Yup.addMethod(Yup.string, 'fractionNumber', function (...args) {
            const [message] = args;
            return this.test({
                name: 'test-fraction-number',
                message: message,
                exclusive: true,
                params: {},
                test: function (val) {
                    if ((val === undefined || val === null)) {
                        return true;
                    }
                    if (!val) {
                        return true;
                    }
                    if (val.includes('.')) {
                        return false;
                    }
                    if (/[a-zA-Z]/.test(val)) {
                        return false;
                    }
                    if (val.includes('-')) {
                        const [beforeDash, afterDash] = val.split('-');
                        if (Number.isNaN(Number(beforeDash)) || Number(beforeDash) < 0) {
                            return false;
                        }
                        if (!afterDash || !afterDash.includes('/')) {
                            return false;
                        }
                        const [beforeSlash, afterSlash] = afterDash.split('/');
                        if (Number.isNaN(Number(beforeSlash)) || Number.isNaN(Number(afterSlash)) || Number(beforeSlash) >= Number(afterSlash)) {
                            return false;
                        }
                        if (beforeSlash.includes('.') || beforeSlash.includes('-') || afterSlash.includes('.') || afterSlash.includes('-')) {
                            return false;
                        }
                        if (!/^(2|4|8|16)$/.test(afterSlash)) {
                            return false;
                        }
                        if (Number(beforeSlash) < 0) {
                            return false;
                        }
                    }
                    return true;
                }
            });
        });
    }


    /**
     * Injects all the extensions of this interface to yup.
     */
    public static injectExtensions(): void {
        this.phoneNo();
        this.emailAddress();
        this.validDate();
        this.minDate();
        this.maxDate();
        this.disablePastDate();
        this.disableFutureDate();
        this.maxNumber();
        this.minNumber();
        this.fractionNumber();
    }
}

export * as Yup from "yup";
export default YupExtensions;
